import React, { useState } from 'react';
import { MessageCircle, X, Send, HelpCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';

const FAQ = [
  {
    question: 'Comment créer un service ?',
    answer: 'Rendez-vous sur "Services" puis cliquez sur "Proposer un service". Remplissez le formulaire avec les détails de votre offre.'
  },
  {
    question: 'Comment réserver un service ?',
    answer: 'Trouvez le service qui vous intéresse, cliquez dessus puis sur "Réserver". Vous devez avoir suffisamment de crédits.'
  },
  {
    question: 'Comment fonctionnent les crédits ?',
    answer: '1 heure = 1 crédit par défaut. Vous recevez 10 crédits gratuits à l\'inscription. Les crédits sont transférés automatiquement quand un échange est marqué comme complété.'
  },
  {
    question: 'Comment signaler un problème ?',
    answer: 'Utilisez le bouton "Signaler" sur les services, profils ou réservations. Notre équipe examinera votre signalement.'
  },
  {
    question: 'Contacter le support',
    answer: 'Utilisez notre page de contact ou envoyez un email à support@okni.be'
  }
];

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { type: 'bot', text: 'Bonjour ! Comment puis-je vous aider ?' }
  ]);
  const [input, setInput] = useState('');

  const handleQuickReply = (question) => {
    const faqItem = FAQ.find(f => f.question === question);
    setMessages([...messages, 
      { type: 'user', text: question },
      { type: 'bot', text: faqItem.answer }
    ]);
  };

  const handleSend = () => {
    if (!input.trim()) return;
    
    const userMessage = input;
    const newMessages = [...messages, { type: 'user', text: userMessage }];
    
    const lowerInput = userMessage.toLowerCase();
    let response = 'Je n\'ai pas compris votre question. Veuillez consulter notre FAQ ou contacter le support à support@okni.be';
    
    FAQ.forEach(faq => {
      const keywords = faq.question.toLowerCase().split(' ');
      if (keywords.some(keyword => lowerInput.includes(keyword))) {
        response = faq.answer;
      }
    });
    
    newMessages.push({ type: 'bot', text: response });
    setMessages(newMessages);
    setInput('');
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 w-14 h-14 bg-primary text-white rounded-full shadow-lg hover:scale-110 transition-transform flex items-center justify-center z-[9999]"
        aria-label="Ouvrir le chatbot"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-2 sm:right-6 w-[calc(100vw-1rem)] sm:w-96 h-[500px] z-[9999] shadow-2xl rounded-2xl overflow-hidden">
      <Card className="h-full flex flex-col">
        <CardHeader className="bg-primary text-white p-4 flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            Aide Oknî
          </CardTitle>
          <Button 
            size="icon" 
            variant="ghost" 
            className="text-white hover:bg-white/20"
            onClick={() => setIsOpen(false)}
          >
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        
        <CardContent className="flex-1 overflow-y-auto p-4 space-y-4 bg-muted/20">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] p-3 rounded-xl ${
                  msg.type === 'user'
                    ? 'bg-primary text-white'
                    : 'bg-white dark:bg-slate-800'
                }`}
              >
                <p className="text-sm">{msg.text}</p>
              </div>
            </div>
          ))}
          
          {messages.length === 1 && (
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground mb-2">Questions fréquentes :</p>
              {FAQ.map((faq, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-left h-auto py-2 px-3"
                  onClick={() => handleQuickReply(faq.question)}
                >
                  {faq.question}
                </Button>
              ))}
            </div>
          )}
          
          <p className="text-xs text-muted-foreground text-center pt-4">
            ⚠️ Aide générale uniquement. En cas de danger, contactez le 112.
          </p>
        </CardContent>
        
        <div className="p-4 border-t flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Posez votre question..."
            className="rounded-xl"
          />
          <Button onClick={handleSend} size="icon" className="rounded-xl">
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </Card>
    </div>
  );
}
